<template>
  <div class="audio-player-container">
    <div class="d-flex align-center justify-center">
      <canvas ref="waveformCanvas" width="400" height="80" />
    </div>

    <div class="audio-player-cues" role="status" aria-live="polite" aria-atomic="true">
      <span class="audio-player-sr-only">{{ activeCueAnnouncement }}</span>

      <div class="d-flex justify-center align-center my-1" style="height: 50px;" aria-hidden="true">
        <div v-for="cue in activeCues" v-bind:key="cue">
          <pre>{{ cue }}</pre>
        </div>
      </div>
    </div>

    <div>
      <vue-plyr ref="plyr" v-bind:options="options">
        <audio
          controls
          :aria-label="$t('routes.pod.audioPlayer')"
          crossorigin
          ref="plyrAudioSource"
          v-bind:src="getSource"
          v-on:ready="plyReady"
          v-on:play="plyrPlay"
          v-on:pause="plyrPause"
          v-on:timeupdate="plyrTimeUpdate"
          v-on:volumechange="plyrVolumeChanged"
          v-on:ratechange="plyrSpeedChanged"
        />
      </vue-plyr>
    </div>

    <PlayerControls
      v-model:transcodedAsset="transcodedAsset"
      :comments="comments"
      :show-fullscreen="false"
    />
  </div>
</template>

<script>
import { useCommonStore } from '@/stores/CommonStore.js'
import { usePlayerStore } from '@/stores/PlayerStore.js'
import PlayerControls from './PlayerControls.vue'
import { useAVWaveform } from 'vue-audio-visual';
import { toRef } from 'vue';

export default {
  name: "AudioPlayer",
  components: {
    PlayerControls,
  },
  props: {
    pod: Object,
    useRawFile: {
      type: Boolean,
      required: false,
      default: false,
    },
    comments: {
      type: Array,
      required: false,
    },
    // Controls whether player should persist its settings in the local storage.
    persistSettings: {
      type: Boolean,
      required: false,
    },
  },
  data: function() {
    return {
      activeCues: [],
      activeCueAnnouncement: '',
    };
  },
  mounted: function() {
    const playerStore = usePlayerStore();
    playerStore.resetPlayer();
    playerStore.refreshSubtitles(this.pod);

    this.visualizeWaveform();
  },
  watch: {
    pod: function(newPod) {
      const playerStore = usePlayerStore();
      playerStore.refreshSubtitles(newPod);
      this.visualizeWaveform();
    },
    getSource: function() {
      const playerStore = usePlayerStore();
      playerStore.resetPlayer();
    },
    // Watch the 'getSeekTimeEvent' value change in the PlayerStore state mngr
    getSeekTimeEvent: function(newValue) {
      if (newValue !== null) {
        this.plyr().currentTime = newValue;
        const playerStore = usePlayerStore();
        playerStore.setSeekTimeEvent(null);
      }
    },
    activeSubtitle: function(activeSub) {
      if (!this.plyr()) {
        return;
      }

      let videoEl = this.plyr().media;
      let currentTracks = videoEl.getElementsByTagName('track');
      for (let i = currentTracks.length -1; i >= 0; i--) {
        videoEl.removeChild(currentTracks[i]);
      }

      // console.log("media", this.plyr().media)

      if (activeSub) {
        // create new track tag
        let track = document.createElement('track');
        track.setAttribute('kind', 'captions');
        track.setAttribute('src', activeSub.url);
        track.setAttribute('label', activeSub.label);
        track.setAttribute('srclang', activeSub.language);
        track.setAttribute('default', 'true');

        videoEl.appendChild(track);

        track.track.oncuechange = (e) => {
          const cues = [];

          for (const cue of e.target.activeCues) {
            cues.push(cue.text);
          }

          this.activeCues = cues;
          this.activeCueAnnouncement = cues.join(' ');
        };

        track.track.mode = 'hidden';
        this.plyr().captions.language = activeSub.language;
      } else {
        this.activeCues = [];
        this.activeCueAnnouncement = '';
      }
    }
  },
  methods: {
    plyr: function() {
      if (this.$refs && this.$refs.plyr && this.$refs.plyr.player) {
        return this.$refs.plyr.player;
      }
      return {};
    },
    plyReady: function(e) {
      this.setDuration(e.target.duration || this.getPodDuration);

      const playerStore = usePlayerStore();
      this.plyr().volume = playerStore.volume;
      this.plyr().muted = playerStore.muted;
      this.plyr().speed = playerStore.speed;
    },
    plyrPlay: function() {
      this.setIsPlaying(true);
    },
    plyrPause: function() {
      this.setIsPlaying(false);
    },
    plyrTimeUpdate: function(e) {
      // set the current time for all components that are interest
      const playerStore = usePlayerStore();
      playerStore.setCurrentTime(e.target.currentTime);
      this.setDuration(e.target.duration || this.getPodDuration);

      // console.log(e.target.currentTime);
      // console.log(e.target.duration);
    },
    plyrVolumeChanged: function(e) {
      const playerStore = usePlayerStore();
      playerStore.setVolume(e.target.volume);
      playerStore.setMuted(e.target.muted);
    },
    plyrSpeedChanged: function(e) {
      const playerStore = usePlayerStore();
      playerStore.setSpeed(e.target.playbackRate);
    },
    setPlayerVolume: function(volume) {
      const playerStore = usePlayerStore();
      playerStore.setVolume(volume);
      this.plyr().volume = volume;
    },
    togglePlayerMuted: function() {
      this.setPlayerMuted(!this.muted);
    },
    setPlayerMuted: function(muted) {
      const playerStore = usePlayerStore();
      playerStore.setMuted(muted);
      this.plyr().muted = muted;
    },
    setIsPlaying: function(value) {
      const playerStore = usePlayerStore();
      playerStore.setIsPlaying(value);
    },
    setDuration: function(duration) {
      const playerStore = usePlayerStore();
      playerStore.setDuration(duration);
    },
    visualizeWaveform: function() {
      // Well, uhm, useAVWaveForm explicitly expects for the player element to be a Ref object.
      useAVWaveform(toRef(this.$refs.plyrAudioSource), this.$refs.waveformCanvas, {
        src: this.getSource,
        playedLineColor: '#3dabff',
        noplayedLineColor: '#3dabff',
        canvWidth: 400,
        canvHeight: 80,
      });
    },
  },
  computed: {
    muted: function() {
      const playerStore = usePlayerStore();
      return playerStore.muted;
    },
    getPodStates: function() {
      const commonStore = useCommonStore();
      return commonStore.podStates;
    },
    getSeekTimeEvent: function() {
      const playerStore = usePlayerStore();
      return playerStore.getSeekTimeEvent;
    },
    activeSubtitle: function() {
      const playerStore = usePlayerStore();
      return playerStore.getActiveSubtitle;
    },
    transcodedAsset: function() {
      if (!this.pod ||
          !this.pod.asset ||
          !this.pod.asset.web ||
          !Array.isArray(this.pod.asset.web)) {
        return {};
      }

      // Get first web asset with status COMPLETE
      for(let i = 0; i < this.pod.asset.web.length; i++) {
        if (this.pod.asset.web[i].state == this.getPodStates.COMPLETE) {
          return this.pod.asset.web[i];
        }
      }

      return {};
    },
    getSource: function() {
      const assetType =  this.useRawFile ? 'raw' : 'web';

      if (!this.pod ||
          !this.pod.asset ||
          !this.pod.asset[assetType] ||
          !Array.isArray(this.pod.asset[assetType])) {
        return '';
      }

      // Get first web/raw asset with status COMPLETE
      for(let i = 0; i < this.pod.asset[assetType].length; i++) {
        if (this.pod.asset[assetType][i].state == this.getPodStates.COMPLETE) {
          return this.pod.asset[assetType][i].url;
        }
      }

      return '';
    },
    getPodDuration: function() {
      if (!this.pod ||
          !this.pod.asset ||
          !this.pod.asset.web ||
          !Array.isArray(this.pod.asset.web)) {
        return 0;
      }

      // Get first web asset with status COMPLETE
      for(let i = 0; i < this.pod.asset.web.length; i++) {
        if (this.pod.asset.web[i].state == this.getPodStates.COMPLETE) {
          return this.pod.asset.web[i].duration;
        }
      }

      return 0;
    },
    options: function() {
      return {
        controls: "<div id='defaultControls' class='plyr__controls'></div>",
        captions: { active: true, language: 'auto', update: true },
        storage: this.persistSettings === true,
      };
    },
  }
}
</script>

<style>
.audio-player-container {
  width: 100%;
  border-radius: 10px;
  padding: 20px;

  background-color: rgb(var(--v-theme-tw-surface));
}

.audio-player-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* .v-theme--dark .audio-player-container {
    background-color: #232b36;
  }
.v-theme--light .audio-player-container {
  background-color: #FFFFFF;
} */

</style>
